import { Injectable } from "@angular/core";
import { Drivers } from "@ionic/storage";
import { Storage } from "@ionic/storage-angular";

import { recipeType } from "../interfaces/recipe.interface";
import {
  DefaultStorageSettings,
  RecipeStorageEntry,
  SavedRecipesStore,
  StorageSettings,
} from "../interfaces/storage.interface";
import { nullRecipe, defaultRecipes } from "../objects/public-recipes";

@Injectable({
  providedIn: "root",
})
export class StorageManagerService {
  constructor(private storage: Storage) {}

  private readonly savedRecipesStorageKey = "savedRecipesV2";

  private storageObject: Partial<StorageSettings> & Record<string, any> = {};

  private allRecipes: recipeType[] = [];

  public currentRecipe: recipeType;

  private defaultStorageObjectValues: DefaultStorageSettings = {
    decimalPrecision: 3,
    measureType: "grams",
    instructionalView: "detailed",
    instructionalViewHide: false,
    showInfo: false,
    showAdvanced: false,
    isFirstTime: true,
    showGlobalThemeBtn: false,
    theme: "light",
    tableInfo: false,
    keepScreenOn: false,
    showPanDiagram: true,
    defaultCalcUnitType: "uscustomary",
    showReorder: false,
    simpleMode: false,
    showSimpleModeAlert: true,
    pizzaTime: false,
    dateOfInitialization: Date.now(),
    askedToReview: false,
    latestNoticeShown: false,
    detailedNutrition: true,
  };

  public get settings(): Readonly<StorageSettings> {
    return this.storageObject as StorageSettings;
  }

  public get recipes(): readonly recipeType[] {
    return this.allRecipes;
  }

  public get recipe(): recipeType {
    return this.currentRecipe;
  }

  async init(): Promise<void> {
    return new Promise(async (resolve) => {
      this.storage = new Storage({
        driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
      });

      await this.storage.create().then(async (interceptStorage) => {
        await interceptStorage
          .forEach((value, key) => {
            if (this.shouldSkipSettingsCache(key, value)) {
              return;
            }
            if (value === undefined && this.defaultStorageObjectValues[key]) {
              this.storageObject[key] = this.defaultStorageObjectValues[key];
            } else {
              this.storageObject[key] = value;
            }
          })
          .then(() => {
            Object.keys(this.defaultStorageObjectValues).forEach((key) => {
              if (!this.storageObject.hasOwnProperty(key)) {
                interceptStorage.set(key, this.defaultStorageObjectValues[key]);
                this.storageObject[key] = this.defaultStorageObjectValues[key];
              }
            });
          });
        await this.regetAllRecipes(interceptStorage);

        this.addSortIds();

        this.sortRecipes();

        await interceptStorage.get("recipeDraft").then((recipe) => {
          if (recipe) {
            this.currentRecipe = recipe;
          } else {
            this.resetCurrentRecipe(interceptStorage);
          }
        });
        resolve();
        return interceptStorage;
      });
    });
  }

  public checkForReview(): boolean {
    if (this.storageObject["askedToReview"]) {
      return false;
    }

    const twoWeeksMilliseconds = 1209600000;

    const initDate = this.storageObject["dateOfInitialization"];
    const currentDate = Date.now();

    if (currentDate - initDate > twoWeeksMilliseconds) {
      return true;
    } else {
      return false;
    }
  }

  public checkForLatestNotice(): boolean {
    if (this.storageObject["latestNoticeShown"]) {
      return false;
    }
    return true;
  }

  public latestNoticeShown(): void {
    this.saveToStorage("latestNoticeShown", true);
  }

  public reviewDone(): void {
    this.saveToStorage("askedToReview", true);
  }

  public resetReviewDate(): void {
    this.saveToStorage("askedToReview", false);
    this.saveToStorage("dateOfInitialization", Date.now());
  }

  public async regetAllRecipes(storage: Storage = this.storage): Promise<void> {
    const savedRecipesStore = await storage.get(this.savedRecipesStorageKey);

    if (this.isSavedRecipesStore(savedRecipesStore)) {
      this.allRecipes = this.normalizeRecipeEntries(
        savedRecipesStore.recipes.map((recipe) => ({ recipe }))
      );
      await this.persistRecipes(storage);
      return;
    }

    await this.migrateLegacyRecipes(storage);
  }

  private shouldSkipSettingsCache(key: string, value: any): boolean {
    return (
      key === "recipeDraft" ||
      key === this.savedRecipesStorageKey ||
      this.isRecipeValue(value)
    );
  }

  private isRecipeValue(value: any): boolean {
    return !!value && !!value.approach;
  }

  private isSavedRecipesStore(value: any): value is SavedRecipesStore {
    return !!value && Array.isArray(value.recipes);
  }

  private async migrateLegacyRecipes(storage: Storage): Promise<void> {
    const recipes: RecipeStorageEntry[] = [];

    await storage.forEach((recipe, key) => {
      if (
        key !== "recipeDraft" &&
        key !== this.savedRecipesStorageKey &&
        this.isRecipeValue(recipe)
      ) {
        recipes.push({ key, recipe });
      }
    });

    this.allRecipes = this.normalizeRecipeEntries(recipes);
    await this.persistRecipes(storage);
  }

  private normalizeRecipeEntries(entries: RecipeStorageEntry[]): recipeType[] {
    const recipeMap = new Map<string, RecipeStorageEntry>();

    entries.forEach((entry) => {
      if (!entry.recipe) {
        return;
      }

      const recipe = this.hydrateRecipe(entry.recipe, entry.key);

      if (!recipe.title) {
        return;
      }

      const existingEntry = recipeMap.get(recipe.title);

      if (!existingEntry) {
        recipeMap.set(recipe.title, { key: entry.key, recipe });
        return;
      }

      if (
        entry.key === recipe.title &&
        existingEntry.key !== existingEntry.recipe.title
      ) {
        recipeMap.set(recipe.title, { key: entry.key, recipe });
      }
    });

    const recipes = Array.from(recipeMap.values()).map((entry) => entry.recipe);
    this.addSortIds(recipes);

    return recipes.sort((a, b) => (a.sortId ?? 0) - (b.sortId ?? 0));
  }

  private hydrateRecipe(recipe: recipeType, fallbackTitle = ""): recipeType {
    const hydratedRecipe = this.cloneValue(recipe);

    Object.keys(nullRecipe).forEach((key) => {
      if (!hydratedRecipe.hasOwnProperty(key)) {
        hydratedRecipe[key] = this.cloneValue(nullRecipe[key]);
      }
    });

    hydratedRecipe.title = hydratedRecipe.title || fallbackTitle;

    return this.validateSplitValues(hydratedRecipe);
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private hasSortId(recipe: recipeType): boolean {
    return recipe.sortId !== undefined && recipe.sortId !== null;
  }

  private addSortIds(recipes: recipeType[] = this.allRecipes): void {
    let startingInt = recipes.reduce((highestSortId, recipe) => {
      if (!this.hasSortId(recipe)) {
        return highestSortId;
      }

      return Math.max(highestSortId, recipe.sortId as number);
    }, -1);

    recipes.forEach((recipe) => {
      if (this.hasSortId(recipe)) {
        return;
      }

      startingInt++;
      recipe.sortId = startingInt;
    });
  }

  private async persistRecipes(storage: Storage = this.storage): Promise<void> {
    this.addSortIds();
    this.sortRecipes();

    await storage.set(this.savedRecipesStorageKey, {
      version: 2,
      recipes: this.cloneValue(this.allRecipes),
    });
  }

  private upsertRecipe(
    recipe: recipeType,
    fallbackTitle = "",
    replacedTitle = ""
  ): recipeType | undefined {
    const savedRecipe = this.hydrateRecipe(recipe, fallbackTitle);

    if (!savedRecipe.title) {
      return undefined;
    }

    const existingRecipe = this.allRecipes.find(
      (recipe) =>
        recipe.title === replacedTitle || recipe.title === savedRecipe.title
    );

    if (
      existingRecipe &&
      this.hasSortId(existingRecipe) &&
      !this.hasSortId(savedRecipe)
    ) {
      savedRecipe.sortId = existingRecipe.sortId;
    }

    this.allRecipes = this.allRecipes.filter(
      (recipe) =>
        recipe.title !== savedRecipe.title &&
        (!replacedTitle || recipe.title !== replacedTitle)
    );
    this.allRecipes.push(this.cloneValue(savedRecipe));

    return savedRecipe;
  }

  public async getTheme(): Promise<string> {
    return new Promise(async (resolve) => {
      this.storage.get("theme").then((themeFromStorageValue) => {
        resolve(themeFromStorageValue);
      });
    });
  }

  public resetCurrentRecipe(storage: Storage = undefined): void {
    let modifiedNullRecipe: recipeType = JSON.parse(JSON.stringify(nullRecipe));
    modifiedNullRecipe.useMetric =
      this.settings.defaultCalcUnitType === "metric";

    this.currentRecipe = JSON.parse(JSON.stringify(modifiedNullRecipe));

    if (!storage) {
      this.resetSimpleMode();
    } else {
      storage.set("showSimpleModeAlert", false);
      storage.set("simpleMode", false);
    }
  }

  public saveRecipeDraft(recipe: recipeType, recipeReassignment = true): void {
    this.storage.set("recipeDraft", JSON.parse(JSON.stringify(recipe)));
    if (recipeReassignment) {
      this.currentRecipe = JSON.parse(JSON.stringify(recipe));
    }
  }

  public openExistingRecipe(name: string): void {
    let existingRecipe = JSON.parse(JSON.stringify(defaultRecipes[name]));
    existingRecipe = this.validateSplitValues(existingRecipe);
    /* This key is only required for the recipe selector */
    delete existingRecipe["description"];

    this.currentRecipe = JSON.parse(JSON.stringify(existingRecipe));
    this.resetSimpleMode();
  }

  public resetSimpleMode(): void {
    this.toggleStorageBool("simpleMode", true, false);
    this.toggleStorageBool("showSimpleModeAlert", true, true);
  }

  public async saveToStorage(key: string, value: any): Promise<void> {
    if (value !== undefined) {
      this.storageObject[key] = value;
      await this.storage.set(key, value);
    }
  }

  public remove(key: string): void {
    this.storage.remove(key);
    delete this.storageObject[key];
  }

  public getFromStorage(key: string): any {
    return this.storageObject[key];
  }

  public async getRecipeFromDatabase(requestedKey: string): Promise<any> {
    const recipe = this.allRecipes.find(
      (recipe) => recipe.title === requestedKey
    );

    if (recipe) {
      return this.cloneValue(recipe);
    }

    const legacyRecipe = await this.storage.get(requestedKey);

    if (this.isRecipeValue(legacyRecipe)) {
      return this.hydrateRecipe(legacyRecipe, requestedKey);
    }

    return undefined;
  }

  public async toggleStorageBool(
    key: string,
    overrideValue = false,
    providedValue = false
  ): Promise<boolean> {
    if (overrideValue) {
      this.storageObject[key] = providedValue;
    } else {
      this.storageObject[key] = !this.storageObject[key];
    }

    this.storage.set(key, this.storageObject[key]);
    return this.storageObject[key];
  }

  public getAllRecipes(): Array<recipeType> {
    return this.allRecipes;
  }

  public async reorderRecipes(recipes: recipeType[]): Promise<void> {
    recipes.forEach((recipe, index) => {
      recipe.sortId = index;
    });

    this.allRecipes = recipes
      .filter((recipe) => recipe !== undefined)
      .map((recipe) => this.hydrateRecipe(recipe));

    await this.persistRecipes();
  }

  public async alphabetizeRecipes(backward = false): Promise<void> {
    this.allRecipes = this.allRecipes.sort((a, b) =>
      a.title.localeCompare(b.title)
    );

    if (backward) {
      this.allRecipes.reverse();
    }

    await this.reorderRecipes(this.allRecipes);
  }

  public sortRecipes(): void {
    this.allRecipes = this.allRecipes.sort(
      (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0)
    );
  }

  public async validateRecipes(): Promise<void> {
    this.allRecipes = this.normalizeRecipeEntries(
      this.allRecipes.map((recipe) => ({ recipe }))
    );
    await this.persistRecipes();
  }

  public async deleteRecipe(recipeName: string): Promise<void> {
    this.allRecipes = this.allRecipes.filter(
      (recipe) => recipe.title !== recipeName
    );
    await this.persistRecipes();
  }

  public async addRecipe(newName: string, newRecipe: recipeType): Promise<void> {
    if (!newName) {
      return;
    }

    const savedRecipe = this.upsertRecipe(newRecipe, newName);

    if (!savedRecipe) {
      return;
    }

    await this.persistRecipes();

    await this.saveToStorage("originalTitle", savedRecipe.title);

    this.saveRecipeDraft(this.cloneValue(savedRecipe));

    return;
  }

  public async addRecipes(recipes: recipeType[]): Promise<void> {
    recipes.forEach((recipe) => {
      if (!recipe) {
        return;
      }

      this.upsertRecipe(recipe, recipe.title);
    });

    await this.persistRecipes();

    return;
  }

  // backward compatibility
  validateSplitValues(newRecipe: recipeType): recipeType {
    if (!newRecipe.flours) {
      newRecipe.flours = this.cloneValue(nullRecipe.flours);
    }

    if (!newRecipe.waters) {
      newRecipe.waters = this.cloneValue(nullRecipe.waters);
    }

    if (!newRecipe.flourId) {
      newRecipe.flourId = 0;
    }

    if (!newRecipe.waterId) {
      newRecipe.waterId = 0;
    }

    return newRecipe;
  }

  public async saveRecipe(
    recipeName: string,
    recipe: recipeType
  ): Promise<void> {
    this.upsertRecipe(recipe, recipeName, recipeName);
    await this.persistRecipes();
  }

  public async renameRecipe(
    newName: string,
    oldName: string,
    recipe: recipeType
  ): Promise<void> {
    recipe.title = newName;

    delete this.storageObject[oldName];

    this.upsertRecipe(recipe, newName, oldName);
    await this.persistRecipes();
  }

  public checkOriginalTitle(): void {
    const storageOriginalTitle = this.settings.originalTitle;
    if (storageOriginalTitle) {
      this.saveToStorage("originalTitle", storageOriginalTitle);
    } else if (!storageOriginalTitle) {
      this.saveToStorage("originalTitle", this.currentRecipe.title);
    }
  }
}
